import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const PDF_STEPS = [
  { key: 'prepare', label: 'Prepare', icon: FiIcons.FiFileText },
  { key: 'number', label: 'Number', icon: FiIcons.FiHash },
  { key: 'branding', label: 'Branding', icon: FiIcons.FiAward },
  { key: 'build', label: 'Build', icon: FiIcons.FiSettings },
  { key: 'convert', label: 'Convert', icon: FiIcons.FiRefreshCw },
  { key: 'download', label: 'Download', icon: FiIcons.FiDownload },
];

const PDF_THRESHOLDS = [25, 38, 48, 92, 97, 100];

const SYNC_STEPS = [
  { key: 'save', label: 'Save', icon: FiIcons.FiSave },
  { key: 'collect', label: 'Collect', icon: FiIcons.FiFolder },
  { key: 'connect', label: 'Connect', icon: FiIcons.FiLink },
  { key: 'upload', label: 'Upload', icon: FiIcons.FiUploadCloud },
  { key: 'verify', label: 'Verify', icon: FiIcons.FiShield },
  { key: 'complete', label: 'Complete', icon: FiIcons.FiCheck },
];

const SYNC_THRESHOLDS = [10, 35, 60, 60, 90, 100];

function getStepStatus(progress, thresholds) {
  return thresholds.map((threshold, i) => {
    if (progress >= threshold) return 'completed';
    const prevThreshold = i === 0 ? 0 : thresholds[i - 1];
    if (progress >= prevThreshold && progress < threshold) return 'active';
    return 'pending';
  });
}

function formatElapsed(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export default function OperationProgressModal({
  open,
  status,
  type,
  stage,
  description,
  progress,
  message,
  elapsedSeconds,
  error,
  result,
  localSaved,
  prefersReducedMotion,
  onCloseError,
  onRetry,
}) {
  const currentSteps = type === 'sync' ? SYNC_STEPS : PDF_STEPS;
  const currentThresholds = type === 'sync' ? SYNC_THRESHOLDS : PDF_THRESHOLDS;
  const stepStatuses = getStepStatus(progress, currentThresholds);
  const isEstProgress = progress >= 48 && progress <= 88 && status === 'active' && type === 'professionalPdf';
  const showSlowMessage = status === 'active' && (
    (type === 'sync' && elapsedSeconds >= 15) ||
    (type === 'professionalPdf' && elapsedSeconds >= 30)
  );
  const slowMessageText = type === 'sync'
    ? 'Still syncing — Google Sheets may be responding slowly.'
    : 'Still working — larger Statements of Work can take a little longer.';

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          onKeyDown={(e) => { if (e.key === 'Escape' && status === 'error') onCloseError(); }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3 }}
            className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
            onClick={status === 'error' ? onCloseError : undefined}
          />
          <motion.div
            initial={prefersReducedMotion ? { opacity: 1 } : { scale: 0.95, opacity: 0 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { scale: 1, opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { scale: 0.95, opacity: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[560px] max-h-[90dvh] flex flex-col overflow-hidden mx-auto"
            role="dialog"
            aria-modal="true"
            aria-label={type === 'sync' ? 'Save & Sync progress' : 'Professional PDF generation progress'}
          >
            <div className="flex-1 overflow-y-auto p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center text-2xl shrink-0">🛡️</div>
                <div className="min-w-0">
                  <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] leading-none mb-1">
                    {type === 'sync' ? 'Cloud Workspace' : 'Professional Document Generator'}
                  </p>
                  {status === 'success' ? (
                    <p className="text-lg font-black text-green-700">{stage}</p>
                  ) : status === 'error' ? (
                    <p className="text-lg font-black text-red-700">{type === 'sync' ? 'Save & Sync could not be completed' : 'Professional PDF could not be generated'}</p>
                  ) : (
                    <p className="text-lg font-black text-gray-900" aria-live="polite">{stage}</p>
                  )}
                </div>
              </div>

              {status === 'active' && description && (
                <motion.p
                  key={description}
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3 }}
                  className="text-sm text-gray-500 mb-6"
                >{description}</motion.p>
              )}

              {status === 'active' && type === 'professionalPdf' && (
                <div className="flex justify-center mb-6">
                  <motion.div
                    animate={prefersReducedMotion ? {} : { y: [0, -6, 0] }}
                    transition={prefersReducedMotion ? { duration: 0 } : { duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    className="relative"
                  >
                    <div className="w-20 h-24 bg-indigo-50 border-2 border-indigo-200 rounded-lg flex items-center justify-center shadow-md">
                      <SafeIcon icon={FiIcons.FiFileText} className="text-3xl text-indigo-400" />
                    </div>
                    <motion.div
                      animate={prefersReducedMotion ? {} : { scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
                      transition={prefersReducedMotion ? { duration: 0 } : { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center shadow-sm"
                    >
                      <SafeIcon icon={FiIcons.FiSettings} className="text-xs text-white" />
                    </motion.div>
                  </motion.div>
                </div>
              )}

              {status === 'active' && type === 'sync' && (
                <div className="flex justify-center mb-6">
                  <motion.div
                    animate={prefersReducedMotion ? {} : { y: [0, -4, 0] }}
                    transition={prefersReducedMotion ? { duration: 0 } : { duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    className="relative"
                  >
                    <div className="w-20 h-20 bg-emerald-50 border-2 border-emerald-200 rounded-lg flex items-center justify-center shadow-md">
                      <SafeIcon icon={FiIcons.FiCloud} className="text-3xl text-emerald-400" />
                    </div>
                    <motion.div
                      animate={prefersReducedMotion ? {} : { scale: [1, 1.12, 1], opacity: [0.6, 1, 0.6] }}
                      transition={prefersReducedMotion ? { duration: 0 } : { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm"
                    >
                      <SafeIcon icon={FiIcons.FiRefreshCw} className="text-xs text-white" />
                    </motion.div>
                  </motion.div>
                </div>
              )}

              {status === 'success' && (
                <div className="flex justify-center mb-6">
                  <motion.div
                    initial={prefersReducedMotion ? {} : { scale: 0 }}
                    animate={prefersReducedMotion ? { scale: 1 } : { scale: 1 }}
                    transition={prefersReducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 200, damping: 15 }}
                    className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center"
                  >
                    <motion.div
                      initial={prefersReducedMotion ? {} : { scale: 0 }}
                      animate={prefersReducedMotion ? { scale: 1 } : { scale: 1 }}
                      transition={prefersReducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 12, delay: 0.15 }}
                    >
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="text-4xl text-green-600" />
                    </motion.div>
                  </motion.div>
                </div>
              )}

              {status === 'success' && result && (
                <div className={`border rounded-xl p-4 mb-6 text-center ${type === 'sync' ? 'bg-emerald-50 border-emerald-200' : 'bg-green-50 border-green-200'}`}>
                  {type === 'professionalPdf' && result.documentNumber ? (
                    <>
                      <p className="text-sm font-bold text-green-800">{result.documentNumber}</p>
                      <p className="text-xs text-green-600 mt-1">{result.fileName}</p>
                    </>
                  ) : (
                    <p className="text-sm font-bold text-green-800">{result}</p>
                  )}
                </div>
              )}

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{isEstProgress ? 'Estimated progress' : 'Progress'}</span>
                  <span className="text-xs font-black text-gray-600">{progress}%</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={progress} aria-label={stage}>
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: status === 'error' ? '#ef4444' : status === 'success' ? '#22c55e' : type === 'sync' ? '#059669' : '#4f46e5' }}
                    initial={false}
                    animate={{ width: `${progress}%` }}
                    transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
              </div>

              {status === 'active' && (
                <div className="flex justify-between items-center mb-4 text-xs text-gray-400">
                  <span className="font-mono font-bold">{formatElapsed(elapsedSeconds)}</span>
                  <span className="text-[10px] italic">{type === 'sync' ? 'Syncing your workspace to the cloud.' : 'Google is preparing the document. This usually takes 15–60 seconds.'}</span>
                </div>
              )}

              {showSlowMessage && (
                <p className="text-xs text-amber-600 font-bold mb-4 text-center">{slowMessageText}</p>
              )}

              {status === 'active' && type === 'professionalPdf' && message && (
                <motion.p
                  key={message}
                  initial={prefersReducedMotion ? {} : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.4 }}
                  className="text-xs text-indigo-500 font-medium italic mb-6 text-center"
                >{message}</motion.p>
              )}

              {status !== 'error' && (
                <div className="grid grid-cols-6 gap-2 mb-2">
                  {currentSteps.map((step, i) => {
                    const stepStatus = status === 'success' ? 'completed' : stepStatuses[i];
                    const activeColor = type === 'sync' ? 'bg-emerald-50 border-emerald-400 text-emerald-600' : 'bg-indigo-50 border-indigo-400 text-indigo-600';
                    const completedColor = type === 'sync' ? 'bg-emerald-500 border-emerald-500' : 'bg-green-500 border-green-500';
                    return (
                      <div key={step.key} className="flex flex-col items-center gap-1.5">
                        <motion.div
                          className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${stepStatus === 'completed' ? `${completedColor} text-white` : stepStatus === 'active' ? activeColor : 'bg-gray-50 border-gray-200 text-gray-300'}`}
                          animate={stepStatus === 'active' && !prefersReducedMotion ? { scale: [1, 1.1, 1] } : {}}
                          transition={stepStatus === 'active' && !prefersReducedMotion ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' } : {}}
                        >
                          {stepStatus === 'completed' ? <SafeIcon icon={FiIcons.FiCheck} className="text-xs" /> : <SafeIcon icon={step.icon} className="text-xs" />}
                        </motion.div>
                        <span className={`text-[9px] font-black uppercase tracking-wider ${stepStatus === 'completed' ? (type === 'sync' ? 'text-emerald-600' : 'text-green-600') : stepStatus === 'active' ? (type === 'sync' ? 'text-emerald-600' : 'text-indigo-600') : 'text-gray-300'}`}>
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {status === 'active' && (
                <p className="text-[10px] text-gray-400 text-center mt-4 font-medium">Please keep this tab open while the operation completes.</p>
              )}

              {status === 'error' && error && (
                <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-5 mt-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-red-500 text-xl shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-red-800 mb-1">{type === 'sync' ? 'Save & Sync could not be completed' : 'Professional PDF could not be generated'}</p>
                      {type === 'sync' && localSaved && (
                        <p className="text-sm text-red-700 font-semibold mb-2">Your proposal is saved locally, but cloud backup did not complete.</p>
                      )}
                      <p className="text-xs text-red-600 break-words">{error.message}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {status === 'error' && (
              <div className="bg-gray-50 px-8 py-5 flex justify-end gap-3 shrink-0 border-t border-gray-100">
                <button
                  onClick={onCloseError}
                  className="px-5 py-2.5 text-xs font-black text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 uppercase tracking-widest transition-all focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                >Close</button>
                <button
                  onClick={onRetry}
                  className={`px-6 py-2.5 text-xs font-black text-white rounded-xl uppercase tracking-widest shadow-lg transition-all focus:ring-2 focus:ring-offset-2 ${type === 'sync' ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500 shadow-emerald-100' : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 shadow-indigo-100'}`}
                >Try Again</button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}